import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule';
import Organisation from '@/models/Customers/Organisations/Organisation';
import UxAssociation from '@/models/Customers/Associations/UxAssociation';
import Address from '@/models/Customers/Addresses/Address';
import UpdateOrganisationRequest from '@/models/Customers/Organisations/UpdateOrganisationRequest';
import Customer from '@/models/Customers/Customers/Customer';
import PermissionUtils from '@/lib/permissionUtils';

export interface IOrganisationState {
    loading: boolean;
    loadingAddresses: boolean;
    loadingRelatedCustomers: boolean;
    loadingRelatedRecords: boolean;
    organisation: Organisation;
    organisations: Organisation[];
    organisationsSearch: string;
    totalOrganisationsPages: number;
    addresses: Address[];
    totalAddressesPages: number;
    relatedCustomers: Customer[];
    totalRelatedCustomersPages: number;
    relatedRecords: UxAssociation[];
    totalRelatedRecordsPages: number;
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'OrganisationModule', namespaced: true })
class OrganisationStore extends VuexModule implements IOrganisationState {

    public loading: boolean = true;
    public loadingAddresses: boolean = true;
    public loadingRelatedCustomers: boolean = true;
    public loadingRelatedRecords: boolean = true;
    public organisation: Organisation = new Organisation();
    public organisations: Organisation[] = [];
    public organisationsSearch: string = '';
    public totalOrganisationsPages: number = 1;
    public addresses: Address[] = [];
    public totalAddressesPages: number = 1;
    public relatedCustomers: Customer[] = [];
    public totalRelatedCustomersPages: number = 1;
    public relatedRecords: UxAssociation[] = [];
    public totalRelatedRecordsPages: number = 1;
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getOrganisations(pageNumber: number) {

        this.setLoading(true);
        const searchParameter = this.organisationsSearch ? `&name=${this.organisationsSearch}` : '';

        await ajax.get(`/portal-backend/ux-api/organisations?pageSize=${this.pageSize}&pageNumber=${pageNumber}${searchParameter}`)
        .then(response => {
            this.context.commit("setOrganisations", response.data.organisations);
            this.context.commit("setOrganisationsTotalPages", response.data.records.totalRecords);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async updateOrganisationsSearch(searchText: string) {
        this.context.commit("setOrganisationsSearch", searchText);
    }

    @Action
    async getOrganisation(id: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/ux-api/organisations/${id}`)
        .then(response => {
            this.context.commit("setOrganisation", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async editOrganisation(request: UpdateOrganisationRequest) {

        this.setLoading(true);

        await ajax.patch(`/portal-backend/ux-api/organisations/${this.organisation.id}`, request)
        .then(response => {
            this.context.commit("setOrganisation", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getAddresses(pageNumber: number) {

        this.setLoadingAddresses(true);

        await ajax.get(`/portal-backend/ux-api/organisations/${this.organisation.id}/addresses?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setAddresses", response.data.results);
            this.context.commit("setAddressesTotalPages", response.data.records.totalRecords);
            this.setLoadingAddresses(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingAddresses(false);
        });
    }

    @Action
    async getRelatedCustomers(pageNumber: number) {

        this.setLoadingRelatedCustomers(true);

        await ajax.get(`/portal-backend/ux-api/organisations/${this.organisation.id}/customers?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setRelatedCustomers", response.data.results);
            this.context.commit("setRelatedCustomersTotalPages", response.data.records.totalRecords);
            this.setLoadingRelatedCustomers(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingRelatedCustomers(false);
        });
    }

    @Action
    async getRelatedRecords(pageNumber: number) {

        this.setLoadingRelatedRecords(true);

        await ajax.get(`/portal-backend/ux-api/organisations/${this.organisation.id}/associations?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setRelatedRecords", response.data.associations);
            this.context.commit("setRelatedRecordsTotalPages", response.data.records.totalRecords);
            this.setLoadingRelatedRecords(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingRelatedRecords(false);
        });
    }

    @Action
    public async cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingAddresses = false;
        this.loadingRelatedRecords = false;
        this.loadingRelatedCustomers = false;
        this.organisation = new Organisation();
        this.organisations = [];
        this.organisationsSearch = '';
        this.totalOrganisationsPages = 1;
        this.addresses = [];
        this.totalAddressesPages = 1;
        this.relatedCustomers = [];
        this.totalRelatedCustomersPages = 1;
        this.relatedRecords = [];
        this.totalRelatedRecordsPages = 1;
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingAddresses(loading: boolean) {
        this.loadingAddresses = loading;
    }

    @Mutation
    private async setLoadingRelatedCustomers(loading: boolean) {
        this.loadingRelatedCustomers = loading;
    }

    @Mutation
    private async setLoadingRelatedRecords(loading: boolean) {
        this.loadingRelatedRecords = loading;
    }

    @Mutation
    private async setOrganisation(organisation: Organisation) {
        this.organisation = organisation;
    }

    @Mutation
    private async setOrganisations(response: Organisation[]) {
        if (response) {
            this.organisations = response;
        }
    }

    @Mutation
    private async setOrganisationsSearch(text: string) {
        this.organisationsSearch = text;
    }

    @Mutation
    private async setOrganisationsTotalPages(total: number) {
        this.totalOrganisationsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setAddresses(response: Address[]) {
        if (response) {
            this.addresses = response;
        }
    }

    @Mutation
    private async setAddressesTotalPages(total: number) {
        this.totalAddressesPages =  Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setRelatedCustomers(response: Customer[]) {
        if (response) {
            this.relatedCustomers = response;
        }
    }

    @Mutation
    private async setRelatedCustomersTotalPages(total: number) {
        this.totalRelatedCustomersPages =  Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setRelatedRecords(response: UxAssociation[]) {
        if (response) {
            this.relatedRecords = response;
        }
    }

    @Mutation
    private async setRelatedRecordsTotalPages(total: number) {
        this.totalRelatedRecordsPages =  Math.ceil(total/this.pageSize);
    }

}

export const OrganisationModule = getModule(OrganisationStore);